import 'reflect-metadata';
import { render } from 'lit-html';

/**
 * Simple one file, one class WebComponent library
 * 
 */

const META_ATT_MAP = Symbol("component-property-attribute_map");
const META_TEMPLATE = Symbol("component-render-template");
const META_EXPOSED = Symbol("component-exposed-methods");
const META_ATT_CONVERT = Symbol("component-property-converter");

export type ComponentPropertyAttributeMap = { [prop: string]: string };
export type MetaInitialCb<R, T> = (target: T, property?: string | symbol) => R;
export type MetaInitial<R, T> = R | MetaInitialCb<R, T>;
export type NextTick = (fn: Function) => void;

export type OnUnmountCallback = () => void;

export interface IComponent {
  root?: DocumentFragment
  render(): Promise<unknown> | unknown;
  onMount?(): OnUnmountCallback | void;
  onRender?(): Promise<void> | void
}

// See: https://github.com/microsoft/TypeScript/issues/22815
export abstract class BaseComponent implements IComponent {
  root!: DocumentFragment
}
export interface BaseComponent extends IComponent { }
export interface IComponentClass {
  new(...args: any[]): IComponent;
}

export interface IBaseComponent extends HTMLElement { 
  isReady?:boolean
 }

export interface ComponentRenderer {
  (component: IBaseComponent, html: unknown, element: Element | DocumentFragment): Promise<void> | void
}

export const nextTick: NextTick = Reflect.has(window, 'setImmediate') ? Reflect.get(window, 'setImmediate') : (fn: Function) => setTimeout(fn, 0);

const CompToClsMap = new WeakMap<IComponent, IBaseComponent>();
const shadowRoots = new WeakMap();

const defaultRenderer = (component: IBaseComponent, html: unknown, element: Element | DocumentFragment) => {
  element.textContent = '';
  if (typeof html === 'string') {
    element.append(html);
  }
}

/**
 * Handy one function meta get/set helper. Adds metadata to a target if its metadata
 * key is not found.
 * @param key The metadata key
 * @param target The object to attach metadata to
 * @param property When working with properties, defines the property to attach the metadata to.
 * @param metaInitial An initializer callback when the metadata isn't found.
 * @example
 *  let obj = { prop: "value" };
 *  let myMeta = meta(Symbol("MYMETA"), obj, "prop", (target, property) => "some metadata");
 *  // now myMeta contains "some metadata" on the first call.
 */
export function meta<R, T>(
  key: Symbol,
  target: T,
  property?: string | symbol | null,
  metaInitial?: MetaInitial<R, T>): R {
  let hasMeta = property ? Reflect.hasMetadata(key, target, property) : Reflect.hasMetadata(key, target);
  if (hasMeta) {
    return property ? Reflect.getMetadata(key, target, property) : Reflect.getMetadata(key, target);
  }

  if (metaInitial) {
    let init: R;
    if (typeof metaInitial === 'function') {
      if (property) {
        init = (metaInitial as MetaInitialCb<R, T>)(target, property);
      } else {
        init = (metaInitial as MetaInitialCb<R, T>)(target);
      }
    } else {
      init = metaInitial;
    }

    if (property) {
      Reflect.defineMetadata(key, init, target, property);
      return Reflect.getMetadata(key, target, property);
    } else {
      Reflect.defineMetadata(key, init, target);
      return Reflect.getMetadata(key, target);
    }
  }

  throw new Error("Invalid Invocation. No meta initializer parameter defined.");
}

/**
 * Simple key/value map. Used to quickly find translations
 */
export class KeyValueMap {
  public map: ComponentPropertyAttributeMap = {};
  set(prop: string, att: string) {
    Reflect.set(this.map, prop, att);
  }

  get(prop: string): string {
    return Reflect.get(this.map, prop);
  }

  listAttributes(): string[] {
    return Object.keys(this.map);
  }

  listProperties(): string[] {
    return Object.values(this.map);
  }
}

export function PropBool() {
  return function(target: IComponent, property: string) {
    meta(META_ATT_CONVERT, target.constructor, property, () => (value:any) => `${value}`.toLowerCase() !== "false" && `${value}` !== "0" && value );
    return Prop()(target, property);
  }
}

export function PropJSON() {
  return function(target: IComponent, property: string) {
    meta(META_ATT_CONVERT, target.constructor, property, () => (value:string) => {
      try { 
        return value?JSON.parse(value):undefined 
      } catch(err) {
        console.warn(err);
        return undefined;
      }  
    });
    return Prop()(target, property);
  }
}

export function Prop() {

  return function (target: IComponent, property: string) {
    // transform camelcase to attribute style name
    // ex:
    // dataAttribute -> data-attribute
    const validAttributeName = property.replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/([0-9])([^0-9])/g, '$1-$2')
      .replace(/([^0-9])([0-9])/g, '$1-$2')
      .replace(/-+/g, '-')
      .toLowerCase();

    // adds attribute to property map to instance metadata
    const propAttMap = meta(META_ATT_MAP, target.constructor, null, () => new KeyValueMap());
    propAttMap.set(validAttributeName, property);
  }
}

export function Expose() {
  return function(target: IComponent, member: string) {
    const exposedMap = meta(META_EXPOSED, target.constructor, null, () => new KeyValueMap());
    exposedMap.set(member, member);
  }
}

/**
 * Utility class used to hide internal interfaces that should not be exposed
 * to client code
 */
export class Privates<KEY extends {}, INTERNAL> {
  private map = new WeakMap<KEY, INTERNAL>();

  get(inst: KEY): INTERNAL {
    return this.map.get(inst)!;
  }

  set(inst: KEY, value: INTERNAL) {
    this.map.set(inst, value);
  }
}

/**
 * Internal interface to base component
 */
interface IPrivateBaseComponent {
  onUnmount?: OnUnmountCallback
  isMounted: boolean
  instance: IComponent
  attrObserver?: MutationObserver
  renderer?: ComponentRenderer
  queueRenderPromise?: Promise<void>
  renderImpl?: () => Promise<unknown>
  onRenderImpl?: () => Promise<void>
  onRenderPromise?: Promise<void>
  onRenderResolve?: () => void
  onRenderReject?: (response?: any) => void
  props: { [name: string]: any }
  newOnRenderPromise: () => void
  updateInstanceProp(prop: string): void
  wrapProp(prop: string | symbol): void
  queueRender(): Promise<void>
  render(): Promise<void>
  onRender(): Promise<void>
}

/**
 * Creates a private interface storage for IBaseComponent.
 */
const priv = new Privates<IBaseComponent, IPrivateBaseComponent>();

export function Component(tag: string, open?: boolean) {
  return function (TargetCls: IComponentClass) {
    customElements.define(tag, class extends HTMLElement implements IBaseComponent {
      public isReady = false;

      constructor() {
        super();

        // track shadowRoot open and closed
        if (open !== undefined) {
          const shadowRoot = this.attachShadow({ mode: open ? "open" : "closed" });
          shadowRoots.set(this, shadowRoot);
        }

        const comp = this;
        const instance = new TargetCls();
        // Defines an internal private interface to hide implementation details
        // from client side access.
        priv.set(comp, {
          props: {},

          isMounted: false,
          // This class is a facade pattern to TargetCls.
          // Instantiate to issue api commands.
          instance,

          newOnRenderPromise: () => {
            const p = priv.get(this);
            p.onRenderPromise = new Promise<void>((resolve, reject) => {
              p.onRenderResolve = resolve;
              p.onRenderReject = reject;
            });

            // reset promise on success or failure
            p.onRenderPromise
              .then(() => {
                p.newOnRenderPromise();
              })
              .catch((err) => {
                console.log("Current render promise failed", err);
                p.newOnRenderPromise();
              });
          },

          updateInstanceProp: (prop: string) => {
            const p = priv.get(comp);
            const attMap = meta<KeyValueMap, Function>(META_ATT_MAP, instance.constructor, null, () => new KeyValueMap());
            let property = attMap?.get(prop);
            if (property && Reflect.has(instance, property)) {
              const value = comp.getAttribute(prop);
              const converter = meta(META_ATT_CONVERT, instance.constructor, property, () => (v:any) => v);
              Reflect.set(instance, property, converter(value || ""));
            }
          },

          wrapProp: (property: string) => {
            const p = priv.get(comp);
            const converter = meta(META_ATT_CONVERT, this.constructor, property, () => (v:any) => v);
            // copy prop value from instance
            let val = 
            p.props[property] = Reflect.get(instance, property);

            // then replace it with a getter/setter so we may trigger re-renders
            Object.defineProperty(instance, property, {
              get(): any {
                return p.props[property];
              },
              set(value: any) {
                value = converter(value);
                let val = p.props[property];
                const update = value != val;
                if (update) {
                  p.props[property] = value;
                  if (comp.isReady) {
                    p.queueRender();
                  }
                }
              },
              enumerable: true,
              configurable: true
            });
          },

          queueRender: async () => {
            const p = priv.get(comp);

            if (!p.queueRenderPromise) {
              p.queueRenderPromise = new Promise((resolve, reject) => {
                nextTick(() => p.render()
                    .then(resolve)
                    .then(() => {
                      p.queueRenderPromise = undefined;
                      if ( p.onRenderResolve ) {
                        p.onRenderResolve();
                      }
                    })
                    .catch((err) => {
                      if ( p.onRenderReject) {
                        p.onRenderReject(err);
                      }
                    return reject(err);
                  })
                )
              });
            }

            return await p.queueRenderPromise;
          },

          render: async () => {
            const p = priv.get(comp);

            if (p.isMounted) {
              const root = shadowRoots.get(comp) || comp;
              const result = await Promise.resolve(p.renderImpl ? p.renderImpl() : '');

              // caches renderer method. Used to swap templating library.
              if (p.renderer === undefined) {
                p.renderer = meta<ComponentRenderer, IComponentClass>(META_TEMPLATE, TargetCls) || defaultRenderer;
              }

              await Promise.resolve(p.renderer(comp, result, root));

              if (p.onRenderImpl) {
                await p.onRenderImpl();
              }
            }
          },

          onRender: async () => {
            const p = priv.get(comp);
            return await Promise.resolve(p.onRenderPromise);
          }
        });

        const p = priv.get(this);
        p.instance.root = shadowRoots.get(this) || this;
        CompToClsMap.set(p.instance, this);
        p.newOnRenderPromise();
      }

      connectedCallback() {
        const p = priv.get(this);

        // Observe attribute changes and update component instance properties on changes.
        p.attrObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === "attributes" && mutation.attributeName) {
              p.updateInstanceProp(mutation.attributeName);
            }
          });
        });

        // Cache implementation renderer
        if (Reflect.has(p.instance, 'render')) {
          p.renderImpl = Reflect.get(p.instance, 'render').bind(p.instance);
        }
        // Replace implementation renderer with debounce version
        Reflect.set(p.instance, 'render', () => p.queueRender());

        // Cache implementation OnRender callback
        if (Reflect.has(p.instance, 'onRender')) {
          p.onRenderImpl = Reflect.get(p.instance, 'onRender').bind(p.instance);
        }
        // Replace implementation OnRender callback with debounce version
        Reflect.set(p.instance, 'onRender', () => p.onRender());

        // build exposed members
        const exposedMap = meta<KeyValueMap, Function>(META_EXPOSED, p.instance.constructor, null, () => new KeyValueMap());
        // assign a proxy method to our exposed methods
        exposedMap.listAttributes().forEach((member) => {
          Reflect.set(this, member,
            (...args: any[]) => {
              return Reflect.apply(Reflect.get(p.instance, member), p.instance, args);
            });
        })
        // kick off attribute observer
        p.attrObserver.observe(this, { attributes: true });


        // Get list of attributes, and assign values from attributes
        const attMap = meta<KeyValueMap, Function>(META_ATT_MAP, p.instance.constructor, null, () => new KeyValueMap());
        if (attMap) {
          Object.keys(attMap?.map).forEach((prop) => {
            p.wrapProp(prop);
            p.updateInstanceProp(prop);
          })
        }

        // flag component as ready.
        p.isMounted = true;
        this.isReady = true;

        p.queueRender()
          .then(() => {
            if (p.instance.onMount) {
              p.onUnmount = p.instance.onMount() || undefined;
            }
          });

      }

      disconnectedCallback() {
        const p = priv.get(this);

        if (p.onUnmount) {
          p.isMounted = false;
          p.onUnmount();
        }

        if (p.attrObserver) {
          p.attrObserver.disconnect();
          p.attrObserver = undefined;
        }
      }
    });
  }
}

/**
 * Injects LitHtml templating support on render() method.
 * @param target IComponent class
 */
export function Lit() {
  return function(target: IComponentClass) {
    meta<ComponentRenderer, IComponentClass>(
      META_TEMPLATE,
      target,
      null,
      () =>
        (component: IBaseComponent, html: unknown, element: Element | DocumentFragment) => {
          render(html, element, { eventContext: component })
        }
    );
  }
}